import { hasTauriRuntime } from './environment'

export function isDesktopConsoleBindingAvailable() {
  return hasTauriRuntime()
}

async function getInvoke() {
  if (!hasTauriRuntime()) {
    return null
  }
  const module = await import('@tauri-apps/api/core')
  return module.invoke
}

export interface DesktopConsoleBindingSummary {
  consoleUrl: string
  instanceId: string
  instanceOrganizationId: string
  fixedOrganizationId: string
  consoleScopedInstance: boolean
  instanceName?: string | null
  organizationName?: string | null
  userEmail?: string | null
  userDisplayName?: string | null
  updatedAt: number
}

export interface DesktopConsolePairingStart {
  consoleUrl: string
  deviceCode: string
  userCode: string
  verificationUri: string
  expiresIn: number
  interval: number
}

export type DesktopConsolePairingPollResult =
  | {
      status: 'pending'
    }
  | {
      status: 'paired'
      binding: DesktopConsoleBindingSummary
    }

export async function getDesktopConsoleBinding(): Promise<DesktopConsoleBindingSummary | null> {
  const invoke = await getInvoke()
  if (!invoke) {
    return null
  }

  try {
    return await invoke<DesktopConsoleBindingSummary | null>('get_console_binding')
  } catch {
    return null
  }
}

export async function clearDesktopConsoleBinding() {
  const invoke = await getInvoke()
  if (!invoke) {
    throw new Error('Desktop console binding is unavailable in this environment')
  }

  await invoke('clear_console_binding')
}

export async function startDesktopConsolePairing(input?: {
  consoleUrl?: string
  deviceName?: string
}): Promise<DesktopConsolePairingStart> {
  const invoke = await getInvoke()
  if (!invoke) {
    throw new Error('Desktop console pairing is unavailable in this environment')
  }

  return invoke<DesktopConsolePairingStart>('start_console_pairing', {
    consoleUrl: input?.consoleUrl,
    deviceName: input?.deviceName,
  })
}

export async function pollDesktopConsolePairing(input: {
  consoleUrl?: string
  deviceCode: string
}): Promise<DesktopConsolePairingPollResult> {
  const invoke = await getInvoke()
  if (!invoke) {
    throw new Error('Desktop console pairing is unavailable in this environment')
  }

  return invoke<DesktopConsolePairingPollResult>('poll_console_pairing', {
    consoleUrl: input.consoleUrl,
    deviceCode: input.deviceCode,
  })
}
